import { forwardRef } from "react";
import PropTypes from "prop-types";
import { useTheme, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";

const CustomBoxRoot = styled(Box)(({ theme, ownerState }) => {
  const { palette = {}, borders = {}, boxShadows = {}, spacing } = theme;
  const {
    display = 'block',
    flexDirection,
    justifyContent,
    alignItems,
    flexWrap,
    gap,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    padding,
    margin,
    bgColor,
    color,
    borderRadius,
    shadow,
    border = false,
    borderColor,
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    overflow,
    textAlign,
    hoverEffect = false,
    fullWidth = false,
    fullHeight = false,
  } = ownerState;

  // Background value
  let backgroundValue;
  if (bgColor) {
    backgroundValue = palette[bgColor]?.main ||
      palette[bgColor] ||
      bgColor;
  }

  // Color value
  const colorValue = color ?
    (palette[color]?.main || palette[color] || color) :
    'inherit';

  // Border styles
  const borderValue = border ?
    `1px solid ${borderColor ? (palette[borderColor]?.main || borderColor) : palette.gray?.main || '#CFCFCF'}` :
    'none';

  // Border radius value
  const borderRadiusValue = borders[borderRadius] || borderRadius;

  // Shadow value
  const boxShadowValue = boxShadows[shadow] || shadow;

  // Hover effect
  const hoverStyles = hoverEffect ? {
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: boxShadows.md || '0px 4px 8px rgba(0, 0, 0, 0.1)',
    }
  } : {};

  // Responsive adjustments
  // In CustomBox.js
   const responsiveStyles = {
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100% - 32px)', // Accounts for padding
      margin: '0 auto', // Center the box
      padding: '16px',
      boxSizing: 'border-box', // Include padding in width calculation
      ...(fullWidth && { width: '100%' }),
    }
  };

  return {
    // Layout
    display,
    flexDirection,
    justifyContent,
    alignItems,
    flexWrap,
    gap,
    // Sizing
    width: fullWidth ? '100%' : width,
    height: fullHeight ? '100%' : height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    // Spacing
    padding: padding || spacing(2),
    margin,
    // Styling
    backgroundColor: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
    border: borderValue,
    // Positioning
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    // Other
    overflow,
    textAlign,
    // Effects
    ...hoverStyles,
    // Responsive
    ...responsiveStyles,
  };
});

const CustomBox = forwardRef((props, ref) => {
  const {
    display = 'block',
    flexDirection,
    justifyContent,
    alignItems,
    flexWrap,
    gap,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    padding,
    margin,
    bgColor,
    color,
    borderRadius,
    shadow,
    border = false,
    borderColor,
    position,
    top,
    right,
    bottom,
    left,
    zIndex,
    overflow,
    textAlign,
    hoverEffect = false,
    fullWidth = false,
    fullHeight = false,
    children,
    sx,
    ...rest
  } = props;

  const theme = useTheme();

  return (
    <CustomBoxRoot
      ref={ref}
      ownerState={{
        display,
        flexDirection,
        justifyContent,
        alignItems,
        flexWrap,
        gap,
        width,
        height,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        padding,
        margin,
        bgColor,
        color,
        borderRadius,
        shadow,
        border,
        borderColor,
        position,
        top,
        right,
        bottom,
        left,
        zIndex,
        overflow,
        textAlign,
        hoverEffect,
        fullWidth,
        fullHeight,
      }}
      sx={sx}
      {...rest}
    >
      {children}
    </CustomBoxRoot>
  );
});

CustomBox.propTypes = {
  display: PropTypes.oneOf([
    'block', 'inline', 'inline-block',
    'flex', 'inline-flex', 'grid',
    'inline-grid', 'none'
  ]),
  flexDirection: PropTypes.oneOf([
    'row', 'row-reverse', 'column', 'column-reverse'
  ]),
  justifyContent: PropTypes.oneOf([
    'flex-start', 'flex-end', 'center',
    'space-between', 'space-around', 'space-evenly'
  ]),
  alignItems: PropTypes.oneOf([
    'flex-start', 'flex-end', 'center', 'baseline', 'stretch'
  ]),
  flexWrap: PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
  gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bgColor: PropTypes.string,
  color: PropTypes.string,
  borderRadius: PropTypes.string,
  shadow: PropTypes.string,
  border: PropTypes.bool,
  borderColor: PropTypes.string,
  position: PropTypes.oneOf([
    'static', 'relative', 'absolute', 'fixed', 'sticky'
  ]),
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  zIndex: PropTypes.number,
  overflow: PropTypes.oneOf(['visible', 'hidden', 'scroll', 'auto']),
  textAlign: PropTypes.oneOf(['left', 'right', 'center', 'justify']),
  hoverEffect: PropTypes.bool,
  fullWidth: PropTypes.bool,
  fullHeight: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
};

export default CustomBox;